import React, { useState, useCallback, useRef } from 'react';
import {

  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
  IconButton,
  Card,
  CardContent,
  Alert,
  Divider,
} from '@mui/material';
import { XIcon, CameraIcon, TextAaIcon, LinkIcon, UploadSimpleIcon, ImagesIcon } from '@phosphor-icons/react';
import CustomButton from '../widgets/CustomButton';
import BottomSheet, { BottomSheetDraggableHeader } from './BottomSheet';
import { screenshotProcessingService, ProcessedMovie } from '../../services/screenshotProcessingService';
import { extractMovieTitles, extractMoviesFromLink } from '../../services/functionsApi';
import { glassStyles } from '../../tokens/designTokens';
import { importAnalyticsService, ImportError } from '../../services/importAnalyticsService';
import ImportMoviesLoadingOverlay from '../widgets/ImportMoviesLoadingOverlay';
import { useAuth } from '../../contexts/AuthContext';
import { font } from '../../utils/fontHelper';
import { useKeyboardAware } from '../../hooks/useKeyboardAware';

interface ImportFeedbackData {
  sessionId: string;
  importSource: 'screenshot' | 'text' | 'link';
  importResults: { moviesDetected: number; moviesSuccessful: number; processingTime: number };
}

interface ImportDrawerProps {
  open: boolean;
  onClose: () => void;
  onMoviesDetected?: (movies: ProcessedMovie[], source: 'screenshot' | 'text' | 'link', sourceInfo?: string) => void;
  onImportComplete?: (data: ImportFeedbackData) => void;
}

export type { ImportFeedbackData };

type ImportMode = 'screenshot' | 'text' | 'link' | null;

const ImportDrawer: React.FC<ImportDrawerProps> = ({
  open,
  onClose,
  onMoviesDetected,
  onImportComplete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { isKeyboardOpen, bottomPadding, scrollIntoViewOnFocus } = useKeyboardAware();

  const [selectedMode, setSelectedMode] = useState<ImportMode>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textInput, setTextInput] = useState<string>('');
  const [linkInput, setLinkInput] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Analytics tracking state

  // Reset state when drawer opens/closes
  const handleClose = useCallback(() => {
    setSelectedMode(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setTextInput('');
    setLinkInput('');
    setIsProcessing(false);
    setProcessingStep('');
    setError(null);
    // Do NOT reset feedback state here — it must survive after the drawer closes
    
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    
    onClose();
  }, [previewUrl, onClose]);

  // Handle file selection for screenshots
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image file size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setError(null);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  }, []);

  // Handle file upload button click
  const handleFileUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Process import based on selected mode
  const handleImport = useCallback(async () => {
    if (!selectedMode) return;
    if (!user?.uid) {
      setError('You need to be signed in to import. Please wait for the app to finish loading, then try again.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    // Start analytics session
    const inputSize = selectedMode === 'screenshot' ? selectedFile?.size : 
                     selectedMode === 'text' ? textInput.length :
                     linkInput.length;
    let platform: string | undefined;
    if (selectedMode === 'link') {
      try {
        platform = new URL(linkInput.trim()).hostname;
      } catch {
        platform = undefined;
      }
    }
    
    const sessionId = importAnalyticsService.startImportSession(
      user.uid,
      selectedMode,
      inputSize,
      platform
    );
    const startTime = Date.now();

    // Let Capacitor / WKWebView paint loading UI before heavy async work starts
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    try {
      let detectedMovies: ProcessedMovie[] = [];
      let sourceInfo = '';

      switch (selectedMode) {
        case 'screenshot':
          if (!selectedFile) {
            const error: ImportError = {
              code: 'NO_FILE_SELECTED',
              message: 'Please select an image file',
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            setError('Please select an image file');
            return;
          }
          
          setProcessingStep('Analyzing screenshot...');
          
          try {
            const result = await screenshotProcessingService.processScreenshot(selectedFile);
            detectedMovies = result.detectedMovies;
            sourceInfo = selectedFile.name;
            
            importAnalyticsService.updateMoviesDetected(detectedMovies.length);
            importAnalyticsService.updateMoviesSuccessful(detectedMovies.length);
            
          } catch (screenshotError: any) {
            const error: ImportError = {
              code: 'SCREENSHOT_PROCESSING_FAILED',
              message: screenshotError.message,
              details: { fileSize: selectedFile.size, fileName: selectedFile.name },
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            throw screenshotError;
          }
          
          if (detectedMovies.length === 0) {
            const error: ImportError = {
              code: 'NO_MOVIES_DETECTED',
              message: 'No movies detected in this screenshot',
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            setError('No movies detected in this screenshot. Please try a different image.');
            return;
          }
          
          break;

        case 'text':
          if (!textInput.trim()) {
            const error: ImportError = {
              code: 'NO_TEXT_PROVIDED',
              message: 'Please enter some text containing movie titles',
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            setError('Please enter some text containing movie titles');
            return;
          }
          
          setProcessingStep('Extracting movies from text...');
          
          try {
            detectedMovies = await processTextInput(textInput.trim());
            sourceInfo = 'Text input';
            
            importAnalyticsService.updateMoviesDetected(detectedMovies.length);
            importAnalyticsService.updateMoviesSuccessful(detectedMovies.length);
            
          } catch (textError: any) {
            const error: ImportError = {
              code: 'TEXT_PROCESSING_FAILED',
              message: textError.message,
              details: { textLength: textInput.length },
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            throw textError;
          }
          
          if (detectedMovies.length === 0) {
            const error: ImportError = {
              code: 'NO_MOVIES_IN_TEXT',
              message: 'No movies found in the provided text',
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            setError('No movies found in the provided text. Please check your input.');
            return;
          }
          
          break;

        case 'link':
          if (!linkInput.trim()) {
            const error: ImportError = {
              code: 'NO_LINK_PROVIDED',
              message: 'Please enter a valid URL',
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            setError('Please enter a valid URL');
            return;
          }
          
          setProcessingStep('Fetching page and scanning text & carousel images...');
          
          try {
            detectedMovies = await processLinkInput(linkInput.trim());
            sourceInfo = linkInput.trim();
            
            importAnalyticsService.updateMoviesDetected(detectedMovies.length);
            importAnalyticsService.updateMoviesSuccessful(detectedMovies.length);
            
          } catch (linkError: any) {
            const error: ImportError = {
              code: 'LINK_PROCESSING_FAILED',
              message: linkError.message,
              details: { url: linkInput },
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            throw linkError;
          }
          
          if (detectedMovies.length === 0) {
            const error: ImportError = {
              code: 'NO_MOVIES_IN_LINK',
              message: 'No movies found at the provided link',
              timestamp: new Date()
            };
            importAnalyticsService.recordError(error);
            setError('No movies found at the provided link. Please check the URL.');
            return;
          }
          
          break;
      }

      // Record successful completion
      const processingTime = Date.now() - startTime;

      // Complete analytics session
      await importAnalyticsService.completeImportSession();

      // Capture feedback data — use local `sessionId` var, not `currentSessionId` state
      // (state is stale in this closure; the local var was set synchronously above)
      const feedbackData = sessionId ? {
        sessionId: sessionId,
        importSource: selectedMode as 'screenshot' | 'text' | 'link',
        importResults: {
          moviesDetected: detectedMovies.length,
          moviesSuccessful: detectedMovies.length,
          processingTime,
        },
      } : null;

      // Close drawer first, then navigate (onMoviesDetected), then show feedback.
      // Order matters: handleClose must run before onMoviesDetected triggers navigation
      // so the drawer is gone before the route changes.
      handleClose();
      onMoviesDetected?.(detectedMovies, selectedMode, sourceInfo);

      // Notify parent after navigation has settled — AppLayout never unmounts so
      // setImportFeedback is safe to call from this timeout.
      if (feedbackData) {
        setTimeout(() => onImportComplete?.(feedbackData), 15000);
      }
      
    } catch (err: any) {
      console.error('Import processing error:', err);
      
      // Record generic error if not already recorded
      if (!err.code) {
        const error: ImportError = {
          code: 'UNKNOWN_ERROR',
          message: err.message || 'Failed to process import',
          details: { selectedMode, hasFile: !!selectedFile },
          timestamp: new Date()
        };
        importAnalyticsService.recordError(error);
      }
      
      // Complete analytics session even on error
      await importAnalyticsService.completeImportSession();
      
      setError(err.message || 'Failed to process import. Please try again.');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  }, [selectedMode, selectedFile, textInput, linkInput, onMoviesDetected, onImportComplete, handleClose, user?.uid]);

  // Process text input using Gemini
  const processTextInput = async (text: string): Promise<ProcessedMovie[]> => {
    try {
      // Extract movie titles using Gemini
      const movieTitles = await extractMovieTitles(text, 250);
      
      if (!movieTitles || movieTitles.length === 0) {
        throw new Error('No movie titles found in the provided text');
      }

      // Use the screenshot processing service to search and process the movies
      // This leverages the existing TMDb search and processing logic
      const processedMovies = await screenshotProcessingService.searchAndProcessMovies(movieTitles);
      
      return processedMovies;
    } catch (error: any) {
      console.error('Text processing error:', error);
      throw new Error(error.message || 'Failed to extract movies from text');
    }
  };

  // Process link input (lists + social posts; server runs OCR on carousel slides when needed)
  const processLinkInput = async (url: string): Promise<ProcessedMovie[]> => {
    try {
      new URL(url);
      const result = await extractMoviesFromLink(url.trim(), 250);
      if (!result.movies.length) {
        throw new Error(
          'No movies found at this link. Use a public post; if titles only appear on carousel images, keep the post public so we can read them.'
        );
      }
      const titles = result.movies.map((m) =>
        m.year ? `${m.title} (${m.year})` : m.title
      );
      return screenshotProcessingService.searchAndProcessMovies(titles);
    } catch (error: any) {
      console.error('Link processing error:', error);
      throw new Error(error.message || 'Failed to import from this link');
    }
  };

  // Check if import is ready
  const isImportReady = () => {
    switch (selectedMode) {
      case 'screenshot':
        return !!selectedFile;
      case 'text':
        return textInput.trim().length > 0;
      case 'link':
        return linkInput.trim().length > 0;
      default:
        return false;
    }
  };

  if (isMobile) {
    // Mobile Bottom Sheet
    return (
      <>
      <BottomSheet
        open={open}
        onClose={handleClose}
        maxHeight="90dvh"
        disableWebHistoryBack
      >
      <Box sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Header */}
        <BottomSheetDraggableHeader sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 3,
          pb: 2,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: font(), fontSize: 18 }}>
            Import Movies
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <XIcon />
          </IconButton>
        </BottomSheetDraggableHeader>

        {/* Scroll region + loading overlay (footer stays tappable for Cancel) */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
        <Box sx={{
          px: 3,
          pb: isKeyboardOpen ? `${bottomPadding + 16}px` : 3,
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          transition: 'padding-bottom 0.25s ease-out',
        }}>
          {/* Mode Selection */}
          {!selectedMode && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Choose how you'd like to import movies:
              </Typography>

              {/* Screenshot Option */}
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                  bgcolor: 'rgba(255, 255, 255, 0.01)',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setSelectedMode('screenshot')}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <CameraIcon size={24} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Upload Screenshot
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload your favourite screenshots of movie lists, posters, or carousels.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Text Option */}
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                  bgcolor: 'rgba(255, 255, 255, 0.01)',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setSelectedMode('text')}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <TextAaIcon size={24} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Paste as Text
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Paste movie titles directly as text
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Link Option */}
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: theme.palette.action.hover },
                  bgcolor: 'rgba(255, 255, 255, 0.01)',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setSelectedMode('link')}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <LinkIcon size={24} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Import from Public link
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pinterest boards, IMDB, Letterboxd lists etc.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Selected Mode Content */}
          {selectedMode && (
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                minHeight: isProcessing ? { xs: 300, sm: 320 } : 'auto',
              }}
            >
              {/* Back Button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CustomButton
                  semantic="tertiary"
                  label="← Back to options"
                  size="small"
                  onClick={() => setSelectedMode(null)}
                  disabled={isProcessing}
                />
              </Box>

              {/* Screenshot Mode */}
              {selectedMode === 'screenshot' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    Upload Screenshot
                  </Typography>
                  
                  {!selectedFile ? (
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: theme.palette.divider,
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        bgcolor: 'rgba(0, 0, 0, 0.3)',
                        '&:hover': {
                          borderColor: theme.palette.primary.main,
                          bgcolor: theme.palette.action.hover,
                        },
                      }}
                      onClick={handleFileUploadClick}
                    >
                      <CameraIcon size={48} />
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        Click to select an image
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Supports JPG, PNG, GIF up to 10MB
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'hidden',
                          maxHeight: 300,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          bgcolor: theme.palette.grey[100],
                          mb: 2,
                        }}
                      >
                        <img
                          src={previewUrl!}
                          alt="Preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          {selectedFile.name}
                        </Typography>
                        <CustomButton
                          semantic="tertiary"
                          size="small"
                          onClick={handleFileUploadClick}
                          icon={<ImagesIcon />}
                          label="Change Image"
                        />
                      </Box>
                    </Box>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                </Box>
              )}

              {/* Text Mode */}
              {selectedMode === 'text' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    Paste Movie Titles
                  </Typography>

                  <TextField
                    multiline
                    rows={8}
                    fullWidth
                    onFocus={scrollIntoViewOnFocus}
                    placeholder="Paste your movie titles here. You can paste from any source - one per line or comma-separated..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '16px', // Prevent iOS zoom
                        lineHeight: 1.5,
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'transparent' },
                      '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '2px'
                      },
                      }
                    }}
                  />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Supports movie titles in any format. AI will extract and clean the titles automatically.
                  </Typography>
                </Box>
              )}

              {/* Link Mode */}
              {selectedMode === 'link' && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    Import from Public link
                  </Typography>

                  <TextField
                    fullWidth
                    onFocus={scrollIntoViewOnFocus}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '16px', // Prevent iOS zoom
                        lineHeight: 1.5,
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '12px',
                      '& fieldset': { borderColor: 'transparent' },
                      '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: '2px'
                      },
                      }
                    }}
                    placeholder="List URL from Pinterest, IMDb, Letterboxd…"
                    value={linkInput}
                    onChange={(e) => setLinkInput(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      startAdornment: <Box sx={{ display: 'flex', mr: 1 }}><LinkIcon size={20} /></Box>
                    }}
                  />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Just paste a link from Pinterest, Instagram, IMDb, or Letterboxd, and we will handle the rest. We will instantly scan your boards, posts, and lists to build your perfect watchlist.
                  </Typography>
                </Box>
              )}

              {/* Error Message */}
              {error && !isProcessing && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </Box>

        {isProcessing && selectedMode && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: (t) => t.zIndex.modal,
              overflow: 'hidden',
              pointerEvents: 'auto',
            }}
          >
            <ImportMoviesLoadingOverlay statusText={processingStep} />
          </Box>
        )}
        </Box>

        {/* Fixed Action Bar */}
        {selectedMode && (
          <>
            <Divider />
            <Box sx={{
              display: 'flex',
              gap: 2,
              p: 3,
              bgcolor: theme.palette.background.paper,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}>
              <CustomButton
                semantic="tertiary"
                label="Cancel"
                size="medium"
                onClick={handleClose}
                sx={{ flex: 1 }}
              />
              <CustomButton
                semantic="brand"
                variant="contained"
                label={isProcessing ? 'Processing...' : 'Import'}
                size="medium"
                onClick={handleImport}
                disabled={!isImportReady() || isProcessing}
                icon={<UploadSimpleIcon />}
                sx={{ flex: 2 }}
              />
            </Box>
          </>
        )}
      </Box>
      </BottomSheet>

      </>
    );
  }

  // Desktop Dialog
  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          maxHeight: '85vh',
          ...glassStyles.popup(theme.palette.mode === 'dark'),
        }
      }}
    >
      <DialogTitle sx={{
        fontWeight: 600,
        fontFamily: font(),
        pb: 1,
      }}>
        Import Movies
      </DialogTitle>

      <IconButton
        onClick={handleClose}
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: theme.palette.text.primary,
          zIndex: 4,
        }}
      >
        <XIcon />
      </IconButton>

      <DialogContent sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Mode Selection */}
        {!selectedMode && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Choose how you'd like to import movies:
            </Typography>

            {/* Screenshot Option */}
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: theme.palette.action.hover },
                bgcolor: 'rgba(255, 255, 255, 0.01)',
                borderRadius: '12px',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedMode('screenshot')}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CameraIcon size={24}/>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Upload Screenshot
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Upload an image of a movie list or poster
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Text Option */}
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: theme.palette.action.hover },
                bgcolor: 'rgba(255, 255, 255, 0.01)',
                borderRadius: '12px',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedMode('text')}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <TextAaIcon size={24} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Paste as Text
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Paste movie titles directly as text
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Link Option */}
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': { bgcolor: theme.palette.action.hover },
                bgcolor: 'rgba(255, 255, 255, 0.01)',
                borderRadius: '12px',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setSelectedMode('link')}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <LinkIcon size={24} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    Import from Public link
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Instagram, Pinterest, etc. — captions and carousel slide images
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Selected Mode Content */}
        {selectedMode && (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              height: '50vh',
              overflow: 'auto',
              minHeight: isProcessing ? 320 : undefined,
            }}
          >
            {/* Back Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CustomButton
                semantic="tertiary"
                label="← Back to options"
                size="small"
                onClick={() => setSelectedMode(null)}
                disabled={isProcessing}
              />
            </Box>

            {/* Screenshot Mode */}
            {selectedMode === 'screenshot' && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                  Upload Screenshot
                </Typography>

                {!selectedFile ? (
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: theme.palette.divider,
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      bgcolor: 'rgba(0, 0, 0, 0.3)',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: theme.palette.action.hover,
                      },
                    }}
                    onClick={handleFileUploadClick}
                  >
                    <CameraIcon size={32} />
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Click to select an image
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supports JPG, PNG, GIF up to 10MB
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: 2,
                        overflow: 'hidden',
                        maxHeight: 200,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: theme.palette.grey[100],
                        mb: 2,
                      }}
                    >
                      <img
                        src={previewUrl!}
                        alt="Preview"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {selectedFile.name}
                      </Typography>
                      <CustomButton
                        semantic="tertiary"
                        size="small"
                        onClick={handleFileUploadClick}
                        icon={<ImagesIcon />}
                        label="Change Image"
                      />
                    </Box>
                  </Box>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
              </Box>
            )}

            {/* Text Mode */}
            {selectedMode === 'text' && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                  Paste Movie Titles
                </Typography>

                <TextField
                  multiline
                  rows={6}
                  fullWidth
                  placeholder="Paste your movie titles here. You can paste from any source - one per line or comma-separated..."
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                      borderRadius: '12px',
                    }
                  }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Supports movie titles in any format. AI will extract and clean the titles automatically.
                </Typography>
              </Box>
            )}

            {/* Link Mode */}
            {selectedMode === 'link' && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                  Import from Public link
                </Typography>

                <TextField
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      lineHeight: 1.5,
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                      borderRadius: '12px',
                    }
                  }}
                  placeholder="https://www.instagram.com/p/… or list URL (IMDb, Letterboxd…)"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: <Box sx={{ display: 'flex', mr: 1 }}><LinkIcon size={20} color={theme.palette.text.secondary} /></Box>
                  }}
                />

                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Past public links from Pinterest, IMDb, Letterboxd, and similar list URLs.
                </Typography>
              </Box>
            )}

            {/* Error Message */}
            {error && !isProcessing && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Action Buttons - Desktop */}
      {selectedMode && (
        <DialogActions sx={{ px: 3, pb: 3, position: 'relative', zIndex: 3 }}>
          <CustomButton
            semantic="tertiary"
            label="Cancel"
            size="medium"
            onClick={handleClose}
            sx={{ flex: 1 }}
          />
          <CustomButton
            semantic="brand"
            variant="contained"
            label={isProcessing ? 'Processing...' : 'Import'}
            size="medium"
            onClick={handleImport}
            disabled={!isImportReady() || isProcessing}
            icon={<UploadSimpleIcon />}
            sx={{ flex: 2 }}
          />
        </DialogActions>
      )}

      {isProcessing && selectedMode && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            borderRadius: 1,
            overflow: 'hidden',
            pointerEvents: 'auto',
          }}
        >
          <ImportMoviesLoadingOverlay statusText={processingStep} />
        </Box>
      )}
    </Dialog>

    </>
  );
};

export default ImportDrawer;